import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Upload, FileText, Loader2, Link2, EyeOff, RefreshCw, AlertTriangle } from 'lucide-react';

const FILTROS = [
  { v: 'todos', l: 'Todas' },
  { v: 'vinculada', l: 'Vinculadas' },
  { v: 'ja_existente', l: 'Já existentes' },
  { v: 'sem_correspondencia', l: 'Sem correspondência' },
  { v: 'divergente', l: 'Divergentes' },
];
const COR = {
  vinculada: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  ja_existente: 'bg-slate-50 text-slate-600 border-slate-200',
  sem_correspondencia: 'bg-amber-50 text-amber-700 border-amber-200',
  divergente: 'bg-red-50 text-red-700 border-red-200',
  ignorada: 'bg-slate-50 text-slate-500 border-slate-200',
};
const brl = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
const dt = (s) => (s ? String(s).slice(0, 10).split('-').reverse().join('/') : '—');

export default function FiscalImportacaoNfse() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('config_principal');
  const [jobs, setJobs] = useState([]);
  const [job, setJob] = useState(null);
  const [dados, setDados] = useState(null);
  const [filtro, setFiltro] = useState('todos');
  const [busy, setBusy] = useState('');

  const carregarJobs = useCallback(async () => {
    const { data } = await api.get('/fiscal/importacao/jobs');
    setJobs(data.jobs || []);
  }, []);

  useEffect(() => {
    api.get('/fiscal/empresas').then(({ data }) => setEmpresas(data.empresas || [])).catch(() => {});
    carregarJobs();
  }, [carregarJobs]);

  const abrir = useCallback(async (jobId, f = filtro) => {
    setBusy('abrir');
    try {
      const { data } = await api.get(`/fiscal/importacao/${jobId}/registros`, { params: { filtro: f, limite: 300 } });
      setDados(data);
      setJob(data.job);
    } catch { toast.error('Não foi possível abrir a importação.'); }
    setBusy('');
  }, [filtro]);

  const enviar = async (e) => {
    const arquivos = Array.from(e.target.files || []);
    if (!arquivos.length) return;
    const fd = new FormData();
    fd.append('empresa_id', empresaId);
    arquivos.forEach((a) => fd.append('arquivos', a));
    setBusy('upload');
    try {
      const { data } = await api.post('/fiscal/importacao/nfse', fd);
      const imp = data.importacoes[0];
      if (imp.divergencias?.length) {
        toast.error(`Importação com divergência de totais: ${imp.divergencias.join(' · ')}`);
      } else {
        toast.success(`${imp.novos} nota(s) nova(s) · ${imp.reaproveitados} já existente(s)`);
      }
      await carregarJobs();
      await abrir(imp.job_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao importar o arquivo.');
    }
    setBusy('');
    e.target.value = '';
  };

  const reconciliar = async () => {
    setBusy('conciliar');
    try {
      const { data } = await api.post(`/fiscal/importacao/${job.id}/conciliar-lote`);
      toast.success(`${data.vinculados} vinculada(s) de ${data.reavaliados} reavaliada(s)`);
      await abrir(job.id);
    } catch { toast.error('Falha na conciliação em lote.'); }
    setBusy('');
  };

  const acao = async (reg, acaoNome) => {
    const titulo_id = acaoNome === 'vincular'
      ? window.prompt('ID do título para vincular:', (reg.candidatos || [])[0] || '')
      : null;
    if (acaoNome === 'vincular' && !titulo_id) return;
    try {
      await api.post(`/fiscal/importacao/registros/${reg.id}/conciliar`,
        { titulo_id, acao: acaoNome, motivo: acaoNome === 'ignorar' ? 'Revisado em tela' : null, operador: 'admin' });
      toast.success(acaoNome === 'vincular' ? 'NFS-e vinculada ao título' : 'Registro ignorado');
      await abrir(job.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.detail || 'Não foi possível concluir a ação.');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="fiscal-importacao-nfse">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Importar NFS-e já emitidas</h1>
        <p className="text-sm text-muted-foreground">
          Migra o histórico do sistema anterior sem emitir nada, sem baixar pagamentos e sem mexer nas sequências fiscais.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">1 · Enviar o arquivo</CardTitle>
          <CardDescription>PDF do relatório anterior ou XMLs de NFS-e em lote. Reenviar o mesmo arquivo não duplica notas.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="w-72">
            <label className="text-xs text-slate-500">Empresa emissora</label>
            <Select value={empresaId} onValueChange={setEmpresaId}>
              <SelectTrigger data-testid="import-empresa"><SelectValue /></SelectTrigger>
              <SelectContent>
                {empresas.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.razao_social}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="inline-flex">
            <Input type="file" multiple accept=".pdf,.xml" className="hidden" onChange={enviar}
              data-testid="import-arquivo" />
            <Button asChild disabled={busy === 'upload'}>
              <span>
                {busy === 'upload' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Selecionar PDF / XMLs
              </span>
            </Button>
          </label>
        </CardContent>
      </Card>

      {jobs.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Importações</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            {jobs.slice(0, 8).map((j) => (
              <div key={j.id} className="flex flex-wrap items-center gap-3 rounded bg-slate-50 px-3 py-2">
                <FileText className="w-4 h-4 text-slate-500" />
                <span className="font-medium">{j.arquivo_nome}</span>
                <span className="text-slate-500">{j.totais_lidos?.registros} registro(s)</span>
                <span className="text-slate-500">{brl(j.totais_lidos?.valor_original)} orig. · {brl(j.totais_lidos?.valor_emitido)} emit.</span>
                {j.divergencias?.length > 0 && (
                  <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">
                    <AlertTriangle className="w-3 h-3 mr-1" /> divergente
                  </Badge>
                )}
                <Button size="sm" variant="outline" onClick={() => abrir(j.id)} data-testid={`import-abrir-${j.id}`}>Abrir</Button>
                {j.documento_original && (
                  <a className="text-xs text-blue-700 underline"
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/fiscal/importacao/${j.id}/documento`}
                    target="_blank" rel="noreferrer">documento original</a>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {dados && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between space-y-0">
            <div>
              <CardTitle className="text-lg">2 · Conciliação</CardTitle>
              <CardDescription>
                {job?.arquivo_nome} · lidos {job?.totais_lidos?.registros} / arquivo {job?.totais_arquivo?.registros || '—'}
                {job?.divergencias?.length > 0 && (
                  <span className="text-red-700"> · {job.divergencias.join(' · ')}</span>
                )}
              </CardDescription>
            </div>
            <Button size="sm" variant="outline" onClick={reconciliar} disabled={busy === 'conciliar'}
              data-testid="import-conciliar-lote">
              {busy === 'conciliar' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Conciliar em lote
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {FILTROS.map((f) => (
                <Button key={f.v} size="sm" variant={filtro === f.v ? 'default' : 'outline'}
                  onClick={() => { setFiltro(f.v); abrir(job.id, f.v); }}
                  data-testid={`import-filtro-${f.v}`}>
                  {f.l}{f.v !== 'todos' ? ` (${dados.contagens?.[f.v] ?? 0})` : ''}
                </Button>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-500">
                  <tr className="border-b">
                    <th className="px-2 py-2 text-left">Tomador</th>
                    <th className="px-2 py-2 text-left">Venc.</th>
                    <th className="px-2 py-2 text-left">Competência</th>
                    <th className="px-2 py-2 text-left">RPS</th>
                    <th className="px-2 py-2 text-left">NFS-e</th>
                    <th className="px-2 py-2 text-left">Situação</th>
                    <th className="px-2 py-2 text-right">Original</th>
                    <th className="px-2 py-2 text-right">Emitido</th>
                    <th className="px-2 py-2 text-left">Conciliação</th>
                    <th className="px-2 py-2 text-left">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.registros.map((r) => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="px-2 py-2">{r.tomador_nome}</td>
                      <td className="px-2 py-2">{dt(r.vencimento)}</td>
                      <td className="px-2 py-2">{dt(r.competencia)}</td>
                      <td className="px-2 py-2">{r.rps_numero}</td>
                      <td className="px-2 py-2 font-mono">{r.numero_nfse}</td>
                      <td className="px-2 py-2">
                        {r.situacao_informada}
                        <span className="block text-[10px] text-slate-400">
                          municipal: {r.situacao_municipal || 'não confirmada'}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right">{brl(r.valor_original)}</td>
                      <td className="px-2 py-2 text-right">{brl(r.valor_emitido)}</td>
                      <td className="px-2 py-2">
                        <Badge variant="outline" className={COR[r.conciliacao_status] || ''}>
                          {r.conciliacao_status}
                        </Badge>
                        {r.conciliacao_motivo && (
                          <span className="block text-[10px] text-slate-500">{r.conciliacao_motivo}</span>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        {r.conciliacao_status !== 'vinculada' && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => acao(r, 'vincular')}
                              data-testid={`import-vincular-${r.id}`}>
                              <Link2 className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => acao(r, 'ignorar')}
                              data-testid={`import-ignorar-${r.id}`}>
                              <EyeOff className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-slate-500">
              Títulos só saem da emissão depois do vínculo confirmado. "Valor original menos emitido" nunca
              vira saldo faturável automaticamente.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
